// Dependencies
// Project
import React, { PropsWithChildren } from "react";

import { ColorShade } from "../../app/Theme";
// Styles
import css from "./Text.module.scss";

export const elementTags = ["p", "span", "h1", "h2", "h3", "h4", "h5", "h6", "caption", "label", "div", "li"] as const;
type ElementTag = (typeof elementTags)[number];

export const textSizes = [
	"extra-small",
	"caption",
	"paragraph-3",
	"paragraph-2",
	"paragraph-1",
	"sub-heading",
	"heading-5",
	"heading-4",
	"heading-3",
	"heading-2",
	"heading-1",
] as const;
export type TextSize = (typeof textSizes)[number];

export const textWeights = ["light", "regular", "semi-bold", "bold", "black"] as const;
type TextWeight = (typeof textWeights)[number];

export interface BaseProps {
	/**
	 * The html element to use to render the text. This will override the
	 * default element. The default element is a function of the size prop.
	 */
	element?: ElementTag;
	/**
	 * The size of the text
	 */
	size: TextSize;
	/**
	 * The weight of the text
	 */
	weight: TextWeight;
	/**
	 * Optional color of the text. If not specified, the color will be
	 * inherited from the parent element/
	 */
	color?: ColorShade;
	/**
	 * Optional flag to make text italic
	 */
	italic?: boolean;
	/**
	 * Optional class name to add to the text element
	 */
	className?: string;
	/**
	 * Optional htmlFor attribute to add to the text element. Useful for
	 * binding a label to an input element.
	 */
	htmlFor?: string;
	/**
	 * Optional testID attribute to add to the text element. Useful for
	 * testing.
	 */
	testID?: string;
	/**
	 * Optional flag to enable overflow wrap
	 */
	overflowWrap?: React.CSSProperties["overflowWrap"];
}

/**
 * A react hook to return an element based on the size prop
 *
 * The logic is as follows:
 * - Use the specified element if provided
 * - otherwise, if there is only one child, use a div
 * - finally, chose an element based on the size prop
 *
 * @param element The element to use if specified
 * @param size The size of the text
 * @param children The children of the text element, used to determine if there are multiple children
 *
 * @returns The element to use
 * @deprecated Use py-components Text component
 */
const useElement = (
	element: ElementTag | undefined,
	size: BaseProps["size"],
	children: React.ReactNode
): ElementTag => {
	if (element) {
		return element;
	}

	const isOnlyChild = React.Children.count(children) === 1;
	if (!isOnlyChild) {
		return "div";
	}

	switch (size) {
		case "extra-small":
			return "span";
		case "caption":
			return "p";
		case "paragraph-3":
		case "paragraph-2":
		case "paragraph-1":
			return "p";
		case "sub-heading":
			return "h6";
		case "heading-5":
			return "h5";
		case "heading-4":
			return "h4";
		case "heading-3":
			return "h3";
		case "heading-2":
			return "h2";
		case "heading-1":
			return "h1";
	}
};

/**
 * Typography component to provide consistent text styles.
 *
 * Standardizes the text size, weight and color.
 * @deprecated Use py-components Text component
 */
const Typography = (props: PropsWithChildren<BaseProps>) => {
	const { element, className, size, weight, children, htmlFor, italic = false, testID = "" } = props;

	// guess element based on size and number of children iff element is not specified
	const textElement = useElement(element, size, children);

	const style: React.CSSProperties = {};

	if (props?.overflowWrap) {
		style.overflowWrap = props.overflowWrap;
	}

	if (props.color) {
		style.color = `var(--py-${props.color})`;
	}

	return React.createElement(
		textElement,
		{
			className: `${css.text} ${css[`text-${size}`]} ${css[`text-${weight}`]} ${
				italic && css.italic
			} ${className}`,
			style: style,
			htmlFor: htmlFor,
			"data-testid": testID,
		},
		children
	);
};

/** @deprecated Use py-components Text component */
export default Typography;
